import type { Booking, BookingDog } from './types';
import { vaccineState, parseVaccineDue } from './domain';
import { fmtDate } from '../../lib/dates';

// ---------- Booking flags (at-a-glance markers) ----------
//
// Five markers staff read without opening a booking. Each flag carries its own
// glyph + tint and a scope: HOUSEHOLD flags sit once on the row (first booking,
// keep-apart, a booking-level note); PER-DOG flags sit against the specific dog
// (medication, vaccines due, a dog-level note). The detail string is the full
// text the hover/tap reveals — the whole point is reading the booking in place.
//
// Data sources are the contract in docs/design/booking-flags/decision.md. This
// is the single derivation; the daily desk, bookings table, and booking detail
// all read from it so they can never show different flags for the same booking.

// `viewing` is not derived by deriveBookingFlags below — a viewing has none of
// the fields it reads. It joins the union because it renders through the SAME
// FlagStrip, and a second badge type would be a second set of geometry, tints
// and truncation rules to keep in step with this one.
export type FlagKind = 'first-booking' | 'meds' | 'note' | 'vaccines' | 'mix' | 'food' | 'viewing';
export type FlagTint = 'new' | 'meds' | 'note' | 'vax' | 'mix' | 'food' | 'viewing';
export type FlagScope = 'household' | 'dog';

export interface BookingFlag {
  kind: FlagKind;
  scope: FlagScope;
  icon: string; // Icon component name
  tint: FlagTint; // CSS tint class: .flag-{tint}
  label: string; // short label (detail view, aria)
  detail: string; // full text revealed on hover/tap
  dogId?: string;
  dogName?: string;
}

function hasText(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

// The flag fields the derivation actually reads — structural, so a saved
// Booking/BookingDetail row, the lean list payload, AND the booking form's
// live edit state (create mode has no saved row) all satisfy it.
type FlaggableDog = Pick<BookingDog, 'id' | 'name'> & {
  vaccinesDue?: string | null;
  meds?: string | null;
  notes?: string | null;
  // THE STAY'S OWN ANSWER (per-dog-care-flags, 0034). Null is nobody-asked
  // and the record decides, as it always did; a boolean is the desk's word
  // for THIS stay and outranks the record.
  needsMeds?: boolean | null;
  // What the dog was on when it arrived — the stay's own copy, stamped at
  // check-in. Preferred over the live record for the words.
  medsAtArrival?: string | null;
};

/** Is this dog on medication THIS stay — the row where it answers, the record where it does not. */
function onMeds(dog: FlaggableDog): boolean {
  return dog.needsMeds == null ? hasText(dog.meds) : dog.needsMeds === true;
}

/**
 * The words for the chip: arrival's copy where the stay has one — even an
 * EMPTY one, which is still the stay's own answer — then the record, then the
 * bare fact. A stamp is a stamp when it is not null; '' means "arrived, and
 * nothing to give", and the record's later words must not show through it.
 */
function medsWords(dog: FlaggableDog): string {
  if (dog.medsAtArrival != null) return hasText(dog.medsAtArrival) ? dog.medsAtArrival.trim() : 'Medication this stay';
  if (hasText(dog.meds)) return dog.meds.trim();
  return 'Medication this stay';
}
export interface CarriedNote {
  text: string;
  fromCheckIn: string; // ISO YYYY-MM-DD of the booking the note came from
}
export type FlaggableBooking = Pick<Booking, 'checkIn'> &
  Partial<Pick<Booking, 'mixOk' | 'foodAtHouse' | 'meds'>> & {
    ownerVisits?: number | null;
    notes?: string | null;
    carriedNote?: CarriedNote | null;
    dogs: FlaggableDog[];
  };

function vaccineDetail(dog: FlaggableDog, stayStartIso: string): string | null {
  const state = vaccineState(dog.vaccinesDue, stayStartIso);
  if (state === 'ok') return null;
  if (state === 'missing') return 'No vaccination on file';
  if (state === 'unreadable') return 'Vaccination record needs checking';
  const due = parseVaccineDue(dog.vaccinesDue);
  return due ? `Vaccination due (lapsed before check-in)` : 'Vaccination due';
}

/**
 * THE ORDER A CROWDED STRIP KEEPS — most consequential first.
 *
 * Where the cell can only seat two chips, which two it seats is the whole of
 * the design. New and Viewing come first because they say what KIND of row this
 * is; then the warnings, in the order they have to be acted on — a vaccine out
 * of date and a dog on medication before the animal is through the door, the
 * food arrangement afterwards. Anything unlisted sorts last, so a new kind of
 * flag degrades to the end rather than displacing a warning.
 */
const DESK_FLAG_PRIORITY: FlagKind[] = [
  // NEW LEADS, always, and sits closest to the name (Sir, 2026-08-27). It is
  // the one tag that changes how the whole row is handled rather than adding a
  // caution to it: a household nobody has met needs checking in differently
  // from one that has been coming for years.
  'first-booking',
  'viewing',
  'vaccines',
  'meds',
  'mix',
  'note',
  'food',
];

export function byDeskPriority(a: BookingFlag, b: BookingFlag): number {
  const rank = (f: BookingFlag) => {
    const i = DESK_FLAG_PRIORITY.indexOf(f.kind);
    return i === -1 ? DESK_FLAG_PRIORITY.length : i;
  };
  return rank(a) - rank(b);
}

export function deriveBookingFlags(b: FlaggableBooking): BookingFlag[] {
  const flags: BookingFlag[] = [];

  // ----- Household flags (once per booking) -----
  if (b.ownerVisits === 1) {
    flags.push({
      kind: 'first-booking',
      scope: 'household',
      icon: 'first-booking',
      tint: 'new',
      label: 'First booking',
      detail: "This household's first stay with us",
    });
  }
  // Keep apart reads the BOOKING flag only (household do_not_mix retired,
  // 0013 ported it): an explicit per-stay opt-out (mixOk true) must not be
  // overridden by the legacy owner value — the tile is the source of truth.
  if (b.mixOk === false) {
    flags.push({
      kind: 'mix',
      scope: 'household',
      icon: 'do-not-mix',
      tint: 'mix',
      label: 'Keep apart',
      detail: 'Keep apart this stay',
    });
  }
  // Care flags (staff-food-at-house): booking-level real columns — the modal
  // checkboxes are the source for the staff Today cards and these chips.
  if (b.foodAtHouse) {
    flags.push({
      kind: 'food',
      scope: 'household',
      icon: 'bowl',
      tint: 'food',
      label: 'Food at house',
      detail: "Owner's food is stored at the house",
    });
  }
  // ONE MEDICATION WARNING, NOT TWO. `bookings.meds` is a tick meaning "somebody
  // here needs meds"; a dog's own meds field says WHO and WHAT. Raised together
  // they are the same warning twice — the booking modal showed "Medication" and
  // "Medication — Ralph" side by side (Sir, 2026-08-27) — and the second is the
  // one worth reading. The tick still speaks when no dog carries any detail,
  // which is the case where it is the only thing that knows.
  const dogsOnMeds = (b.dogs ?? []).some(onMeds);
  if (b.meds && !dogsOnMeds) {
    flags.push({
      kind: 'meds',
      scope: 'household',
      icon: 'medication',
      tint: 'meds',
      label: 'Medication',
      detail: 'A dog on this stay needs meds',
    });
  }
  // Vaccines are aggregated to ONE indicator per booking — every dog whose record
  // needs checking is named in the tooltip ("Vaccines due: Bella, Rex") rather than
  // a syringe per dog. Household-scoped so it sits once on the row.
  const vaxDogs = (b.dogs ?? []).filter((d) => vaccineDetail(d, b.checkIn)).map((d) => d.name);
  if (vaxDogs.length) {
    const names = vaxDogs.join(', ');
    // NO `dogName`. That field is the attribution slot of a DOG-scope flag, and
    // the modal prints it after the label — so a household chip filling it with
    // a LIST rendered as "Vaccines due — Charlie, Nala, Max, Aria", which reads
    // as one dog with four names (Sir, 2026-08-27). The names belong in the
    // detail, which is where every other household flag puts what it knows.
    flags.push({ kind: 'vaccines', scope: 'household', icon: 'vaccine', tint: 'vax', label: 'Vaccines due', detail: names });
  }
  // A note marker fires for the booking's own note, OR — when it has none of its
  // own — for a note inherited from the owner's most recent prior booking. The
  // booking carries the note icon on every surface either way; the detail names
  // the source when it's inherited.
  if (hasText(b.notes)) {
    flags.push({ kind: 'note', scope: 'household', icon: 'note', tint: 'note', label: 'Note', detail: b.notes.trim() });
  } else if (b.carriedNote && hasText(b.carriedNote.text)) {
    flags.push({
      kind: 'note',
      scope: 'household',
      icon: 'note',
      tint: 'note',
      label: 'Note',
      detail: `From the booking on ${fmtDate(b.carriedNote.fromCheckIn)}: ${b.carriedNote.text.trim()}`,
    });
  }

  // ----- Per-dog flags (against the specific dog) -----
  for (const dog of b.dogs ?? []) {
    if (onMeds(dog)) {
      flags.push({
        kind: 'meds',
        scope: 'dog',
        icon: 'medication',
        tint: 'meds',
        label: 'Medication',
        detail: medsWords(dog),
        dogId: dog.id,
        dogName: dog.name,
      });
    }
    if (hasText(dog.notes)) {
      flags.push({
        kind: 'note',
        scope: 'dog',
        icon: 'note',
        tint: 'note',
        label: 'Note',
        detail: dog.notes.trim(),
        dogId: dog.id,
        dogName: dog.name,
      });
    }
  }

  return flags;
}

// ---------- Note carry-forward (AC7) ----------
//
// When a new booking is started for a returning owner, the most recent prior
// booking's note is offered to pre-fill the notes field. "Most recent prior" =
// the owner's bookings ordered by check-in (then created-at) descending, taking
// the first whose note is non-empty. Runs client-side over the owner timeline the
// modal already holds; returns the note and the date it came from so the
// indicator can name it, or null when nothing carries.

export interface PriorNoteBooking {
  checkIn: string;
  createdAt?: string | null;
  notes?: string | null;
}

export function selectCarriedNote(priorBookings: PriorNoteBooking[]): CarriedNote | null {
  const withNote = priorBookings.filter((b) => hasText(b.notes));
  if (withNote.length === 0) return null;
  withNote.sort((a, b) => {
    if (a.checkIn !== b.checkIn) return a.checkIn < b.checkIn ? 1 : -1;
    const ac = a.createdAt ?? '';
    const bc = b.createdAt ?? '';
    return ac < bc ? 1 : ac > bc ? -1 : 0;
  });
  const top = withNote[0]!;
  return { text: top.notes!.trim(), fromCheckIn: top.checkIn };
}

/**
 * THE CHIPS THAT BELONG TO ONE DOG (Freddi, 2026-09-21: "vaccine tags should
 * appear beside the dogs name that has vaccines due ... medication should
 * appear beside the dogs name too, and same with keep apart").
 *
 * WHY THIS IS NOT `deriveBookingFlags`. That one aggregates on purpose: a table
 * ROW has a single line, and four syringes on it say less than one syringe that
 * names four dogs in its tooltip (the note above the vaccines branch records
 * the day that was learned). The booking bar has a line per dog, so it can
 * afford to be specific — and a warning sitting beside the dog it names needs
 * no tooltip at all. Same facts, different surface, different shape.
 *
 * Reads the DOG's own record rather than the stay's ticks: `vaccines_due` is
 * the dog's, `meds` is the dog's, and `keep_apart` is the dog's own answer from
 * registration. The stay-level tiles stay where they are and keep feeding the
 * household chips.
 */
/**
 * THE STAY'S OWN ANSWERS for one dog — the booking's row, or what the open
 * form says this second. Null is nobody-asked and the record decides.
 */
export interface StayAnswers {
  needsMeds?: boolean | null;
  medsAtArrival?: string | null;
  keepApart?: boolean | null;
}

export function dogBarFlags(
  dog: FlaggableDog & { keepApart?: boolean | null },
  stayStartIso: string,
  stay?: StayAnswers | null,
): BookingFlag[] {
  const out: BookingFlag[] = [];
  const vax = vaccineDetail(dog, stayStartIso);
  if (vax) {
    out.push({ kind: 'vaccines', scope: 'dog', icon: 'vaccine', tint: 'vax', label: 'Vaccines due', detail: vax, dogId: dog.id, dogName: dog.name });
  }
  // THE STAY OUTRANKS THE RECORD (PR #228 review, codex 2026-10-06). This read
  // `dog.meds` and nothing else, so with Bran ticked for medication this stay
  // and Nessa alone carrying standing text, the chip sat beside Nessa and the
  // header named the wrong dog. The same rule the strip and the desk rows use:
  // the row's answer where it has one, the record where it does not, and the
  // arrival's copy for the words.
  const onStay = { ...dog, needsMeds: stay?.needsMeds ?? dog.needsMeds, medsAtArrival: stay?.medsAtArrival ?? dog.medsAtArrival };
  if (onMeds(onStay)) {
    out.push({ kind: 'meds', scope: 'dog', icon: 'medication', tint: 'meds', label: 'Medication', detail: medsWords(onStay), dogId: dog.id, dogName: dog.name });
  }
  // ONLY ON A TRUE. Null means nobody has answered — it is not *mixes well* —
  // and a warning raised on an absent answer is a warning about nothing.
  if ((stay?.keepApart ?? dog.keepApart) === true) {
    out.push({ kind: 'mix', scope: 'dog', icon: 'do-not-mix', tint: 'mix', label: 'Keep apart', detail: `${dog.name} is kept apart from other households`, dogId: dog.id, dogName: dog.name });
  }
  return out;
}

/**
 * The bar's per-dog chips for a household: each dog against the stay's own
 * rows, and — once the open form has reported — against what she has ticked
 * since. `live` is null until the form's first report, so a static paint reads
 * the saved rows and never a flash of nothing; after it, the form's dog-scoped
 * medication flags ARE the stay's answer (their detail is already the words),
 * and keep-apart still reads the row.
 */
export function barDogFlags(
  dogs: (FlaggableDog & { keepApart?: boolean | null })[],
  stayStartIso: string,
  rows: ({ id: string } & StayAnswers)[] | null | undefined,
  live: BookingFlag[] | null,
): Map<string, BookingFlag[]> {
  return new Map(
    dogs.map((d) => {
      const row = rows?.find((r) => r.id === d.id) ?? null;
      const liveMeds = live?.find((f) => f.scope === 'dog' && f.kind === 'meds' && f.dogId === d.id);
      const stay: StayAnswers | null = live
        ? { keepApart: row?.keepApart ?? null, needsMeds: !!liveMeds, medsAtArrival: liveMeds ? liveMeds.detail : null }
        : row;
      return [d.id, dogBarFlags(d, stayStartIso, stay)];
    }),
  );
}

/**
 * What is left for the bar's NAME row once the dogs have taken theirs.
 *
 * The note stays here always — it is the household's, not any one dog's, and it
 * is the thing Patricia reads before she does anything else.
 *
 * `shownPerDog` is the set of kinds at least one dog is actually carrying. A
 * kind nobody is carrying KEEPS its household chip: a stay flagged keep-apart
 * with no dog answering for itself must still be visible, and dropping it on
 * the assumption that a dog would show it is exactly how a warning disappears.
 *
 * `first-booking` never survives: the New badge beside the name says it already,
 * and saying it twice in one row is how a badge stops being read.
 */
export function householdBarFlags(flags: BookingFlag[], shownPerDog: Set<FlagKind>): BookingFlag[] {
  return flags.filter((f) => f.scope === 'household' && f.kind !== 'first-booking' && !shownPerDog.has(f.kind));
}
